import React, { useEffect, useState } from 'react';
import { Button, Card, Col, Input, InputNumber, Row, Space, Typography } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import type { ProposalFormField } from '../../types/proposal';

const { Text } = Typography;

interface ProposalFormFieldRowProps {
  field: ProposalFormField;
  readOnly?: boolean;
  onChange?: (value: number | null) => void;
  onLabelChange?: (label: string) => void;
  onNoteChange?: (note: string | null) => void;
}

export const ProposalFormFieldRow: React.FC<ProposalFormFieldRowProps> = ({
  field,
  readOnly = false,
  onChange,
  onLabelChange,
  onNoteChange,
}) => {
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteDraft, setNoteDraft] = useState(field.note ?? '');
  const [labelEditing, setLabelEditing] = useState(false);
  const [labelDraft, setLabelDraft] = useState(field.label ?? '');
  const [rowHover, setRowHover] = useState(false);
  const [inputActive, setInputActive] = useState(false);
  const formatUnit = (unit?: string) => {
    if (!unit) return '';
    return unit.replace(/_/g, ' ');
  };
  const quantity = Number.isFinite(field.quantity) ? field.quantity : 1;
  const isCustomField = Boolean(field.isCustom || field.id?.startsWith('custom_'));
  const labelValue = field.label?.trim() ?? '';
  const displayLabel = labelValue || (isCustomField ? 'Custom item' : field.label);
  const hasNote = Boolean(field.note && field.note.trim());
  const canEditLabel = isCustomField && !readOnly;
  const canConfirmLabel = labelDraft.trim().length > 0;
  const rowActive = rowHover || inputActive || noteOpen;
  const showNoteAction = !readOnly && rowActive && !labelEditing;

  useEffect(() => {
    if (!labelEditing) {
      setLabelDraft(field.label ?? '');
    }
  }, [field.label, labelEditing]);

  const handleOpenNote = () => {
    setNoteDraft(field.note ?? '');
    setNoteOpen(true);
  };

  const handleSaveNote = () => {
    const trimmed = noteDraft.trim();
    onNoteChange?.(trimmed ? trimmed : null);
    setNoteOpen(false);
  };

  const handleCancelNote = () => {
    setNoteOpen(false);
    setNoteDraft(field.note ?? '');
  };

  const handleStartEditLabel = () => {
    if (!canEditLabel) return;
    setLabelDraft(field.label ?? '');
    setLabelEditing(true);
  };

  const handleConfirmLabel = () => {
    if (!canEditLabel || !canConfirmLabel) return;
    onLabelChange?.(labelDraft.trim());
    setLabelEditing(false);
  };

  const handleCancelLabel = () => {
    setLabelEditing(false);
    setLabelDraft(field.label ?? '');
  };

  const handleInputFocus = () => {
    setInputActive(true);
  };

  const handleInputBlur = () => {
    setInputActive(false);
  };

  return (
    <Card
      size="small"
      hoverable
      onMouseEnter={() => setRowHover(true)}
      onMouseLeave={() => setRowHover(false)}>
      <Space direction="vertical" size="small" style={{ width: '100%' }}>
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <Space direction="vertical" size={2}>
              {canEditLabel ? (
                labelEditing ? (
                  <Space direction="vertical" size="small">
                    <Input
                      value={labelDraft}
                      placeholder="Custom item name"
                      onChange={event => setLabelDraft(event.target.value)}
                      onFocus={handleInputFocus}
                      onBlur={handleInputBlur}
                    />
                    <Space size="small">
                      <Button
                        size="small"
                        type="primary"
                        onClick={handleConfirmLabel}
                        disabled={!canConfirmLabel}>
                        OK
                      </Button>
                      <Button size="small" onClick={handleCancelLabel}>
                        Cancel
                      </Button>
                    </Space>
                  </Space>
                ) : (
                  <Space size={8}>
                    <Text strong onClick={handleStartEditLabel}>
                      {displayLabel}
                    </Text>
                    <Button
                      size="small"
                      type="link"
                      icon={<EditOutlined />}
                      onClick={handleStartEditLabel}
                      style={{ opacity: rowActive ? 1 : 0 }}>
                      Edit
                    </Button>
                  </Space>
                )
              ) : (
                <Text strong>{displayLabel}</Text>
              )}
              {!readOnly && (
                <Typography.Link
                  onClick={handleOpenNote}
                  style={{
                    opacity: showNoteAction ? 1 : 0,
                    pointerEvents: showNoteAction ? 'auto' : 'none',
                    transition: 'opacity 150ms ease',
                  }}>
                  {hasNote ? 'Edit note' : 'Add note'}
                </Typography.Link>
              )}
            </Space>
          </Col>
          <Col flex="120px">
            {field.type === 'unit_price' ? (
              <Text type="secondary">
                {quantity} {formatUnit(field.unit)}
              </Text>
            ) : (
              <Text type="secondary"> </Text>
            )}
          </Col>
          <Col flex="200px">
            <Space direction="vertical" size={0} align="end" style={{ width: '100%' }}>
              <InputNumber
                min={0}
                value={field.value ?? null}
                onChange={value => onChange?.(typeof value === 'number' ? value : null)}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                disabled={readOnly}
                placeholder="Total price"
                style={{ width: '100%' }}
              />
            </Space>
          </Col>
        </Row>
        {!readOnly && noteOpen && (
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <Input.TextArea
              value={noteDraft}
              onChange={event => setNoteDraft(event.target.value)}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              placeholder="Add a note for this line item"
              autoSize={{ minRows: 2, maxRows: 4 }}
            />
            <Space size="small">
              <Button size="small" type="primary" onClick={handleSaveNote}>
                OK
              </Button>
              <Button size="small" onClick={handleCancelNote}>
                Cancel
              </Button>
            </Space>
          </Space>
        )}
        {!noteOpen && hasNote && <Text type="secondary">{field.note}</Text>}
      </Space>
    </Card>
  );
};
